import React from 'react';
import _ from 'lodash';
import { daysAgo, handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import store, { brokerActions } from '@apply/adminStore';
import { Accordion, SortableHeader } from '../../common/SharedComponents';
import { BrokerApi } from '@apply/api';
import { ConfirmModal } from '@apply/ui';
import { applicationInsights } from '../../ApplicationInsights';
import { useSelector } from 'react-redux';
import { RefreshEvents } from '../../common/SharedEvents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faTrashCan, faPaperPlane } from '@fortawesome/free-regular-svg-icons';
import { faFlag } from '@fortawesome/free-regular-svg-icons';

const activeColumns = [
    { key: 'Status', label: 'Status', id: 'brokerStatus' },
    { key: 'Flgd', label: 'Flag', id: 'isFlagged' },
    { key: 'Name', label: 'Name' },
    { key: 'Email', label: 'Email', id: 'emailAddress' },
    { key: 'Modified', label: 'Modified', id: 'updatedAt' },
    {
        key: 'Point of Contact',
        label: 'Point of Contact',
        id: 'pointOfContact',
    },
];

const SentBrokerTable = () => {
    const { brokerState, searchState } = useSelector((state) => state);
    const brokerApi = new BrokerApi.BrokerApi(applicationInsights);

    const { dispatch } = store;

    const refreshEvents = new RefreshEvents();

    const [emailBrokerId, setEmailBrokerId] = React.useState(null);
    const [openActivateConfirm, set_openActivateConfirm] = React.useState(false);
    const [openResendConfirm, set_openResendConfirm] = React.useState(false);

    const [deleteBrokerId, set_deleteBrokerId] = React.useState(null);
    const [openDeleteBrokerConfirm, set_openDeleteBrokerConfirm] = React.useState(false);

    const [rows, set_rows] = React.useState([]);
    const [sort, set_sort] = React.useState({
        column: 'brokerStatus',
        direction: 'asc',
    });

    React.useEffect(() => {
        _loadBrokers();

        refreshEvents.addRefreshListener(_loadBrokers);
    }, []);

    const _handleRowClick = (row) => {
        dispatch(brokerActions.setBrokerId({ brokerId: row.id }));
        dispatch(brokerActions.setBrokerModalOpen({ openBrokerModal: true }));
    };

    const _handleResendButton = (brokerId) => {
        setEmailBrokerId(brokerId);
        set_openResendConfirm(true);
    };

    const _handleResendConfirm = async () => {
        try {
            set_openResendConfirm(false);
            await brokerApi.sendBrokerActivationEmail(emailBrokerId);
            handleAdminSuccess('Invite has been resent successfully');
        } catch (ex) {
            handleAdminError(ex, 'An unexpected Error has occurred! Please try again.');
        }
    };

    const _handleActivateConfirm = async () => {
        try {
            const response = await brokerApi.activateBroker(brokerState.brokerId);
            if (response.statusCode !== 200) {
                return;
            }
            handleAdminSuccess('Broker has been Activated successfully');
            set_openActivateConfirm(false);
            refreshEvents.dispatchRefreshEvent();
        } catch (ex) {
            handleAdminError(ex, 'An unexpected Error has occurred! Please try again.');
        }
    };

    const _handleDeleteBrokerButton = (brokerId) => {
        set_deleteBrokerId(brokerId);
        set_openDeleteBrokerConfirm(true);
    };

    const _handleDeleteBrokerConfirm = async () => {
        try {
            set_openDeleteBrokerConfirm(false);
            const response = await brokerApi.deleteBroker(deleteBrokerId);
            if (response.statusCode == 200) {
                handleAdminSuccess('Broker has been Deleted successfully');
                refreshEvents.dispatchRefreshEvent();
            } else {
                throw new Error('An unexpected Error has occurred! Please try again.');
            }
        } catch (ex) {
            handleAdminError(ex, 'An unexpected Error has occurred! Please try again.');
        }
    };

    const _loadBrokers = async () => {
        const response = await brokerApi.getSentBrokerGridRows();

        if (response.statusCode !== 200) return;

        set_rows(
            response.result.map((b) => ({
                ...b,
                name: b.lastName + ', ' + b.firstName,
                updatedDaysAgo: daysAgo(new Date(b.updatedAt)),
            }))
        );
    };

    const matchName = (s) => s.name && s.name.toLowerCase().includes(searchState.searchText.broker.toLowerCase());
    const matchEmail = (s) =>
        s.emailAddress && s.emailAddress.toLowerCase().includes(searchState.searchText.broker.toLowerCase());

    const sortedRows = _.chain(rows)
        .filter((s) => !searchState.searchText.broker || matchName(s) || matchEmail(s))
        .orderBy(sort.column, sort.direction)
        .value();

    return (
        <Accordion
            title='Sent Invite (waiting on broker)'
            isExpanded={false}
        >
            <div className='card'>
                <div className='card-section'>
                    <table>
                        <thead>
                            <tr>
                                <th className='count' />
                                {activeColumns.map((col) => (
                                    <SortableHeader
                                        key={col.key}
                                        column={col}
                                        sort={sort}
                                        onPress={set_sort}
                                    />
                                ))}
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((row) => (
                                <tr key={row.id}>
                                    <td />
                                    <td
                                        className='status'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faEnvelope}
                                            className='icon'
                                        />{' '}
                                        Sent
                                    </td>
                                    <td
                                        className='isFlagged'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.isFlagged && (
                                            <i
                                                style={{ color: 'darkred' }}
                                                title='Broker is Flagged!'
                                            >
                                                <FontAwesomeIcon icon={faFlag} />
                                            </i>
                                        )}
                                    </td>
                                    <td
                                        className='primary nameColumn'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.name}
                                    </td>
                                    <td
                                        className='emailColumn'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.emailAddress}
                                    </td>
                                    <td
                                        className='time-stamp'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.updatedDaysAgo}
                                    </td>
                                    <td onClick={() => _handleRowClick(row)}>{row.pointOfContact}</td>
                                    <td className='actions'>
                                        <button
                                            className='gridButton'
                                            onClick={() => _handleResendButton(row.id)}
                                        >
                                            <FontAwesomeIcon icon={faPaperPlane} /> Resend
                                        </button>
                                        <button
                                            className='gridRedButton'
                                            onClick={() => _handleDeleteBrokerButton(row.id)}
                                        >
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <ConfirmModal
                isOpen={openResendConfirm}
                text='Send an activation email to this user?'
                confirmCallback={_handleResendConfirm}
                cancelCallback={() => set_openResendConfirm(false)}
            />
            <ConfirmModal
                isOpen={openActivateConfirm}
                text='Are you sure you want to activate this user?'
                confirmCallback={_handleActivateConfirm}
                cancelCallback={() => set_openActivateConfirm(false)}
            />
            <ConfirmModal
                isOpen={openDeleteBrokerConfirm}
                text='Delete this broker?'
                confirmCallback={_handleDeleteBrokerConfirm}
                cancelCallback={() => set_openDeleteBrokerConfirm(false)}
            />
        </Accordion>
    );
};

export default SentBrokerTable;
